/**
 * MissedScreen — The scheduled retreat window has passed.
 *
 * M2-UX-P1-015: Shown when device was unavailable at scheduled start.
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import type { EditableRetreatSchedule } from "@retreat/core";
import { formatDuration, totalSeconds } from "../utils/scheduleUtils";
import styles from "./MissedScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

type Props = {
  schedule?: EditableRetreatSchedule | null;
  scheduledAt: number;
  onScheduleAgain: () => void;
  onReturnHome: () => void;
};

export function MissedScreen({
  schedule,
  scheduledAt,
  onScheduleAgain,
  onReturnHome,
}: Props) {
  const date = new Date(scheduledAt);
  const dateLabel = date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const startLabel = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const durationSec = schedule ? totalSeconds(schedule) : 0;
  const endMs = scheduledAt + durationSec * 1000;
  const endDate = new Date(endMs);
  const endLabel = endDate.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return (
    <div className={styles.screen} data-testid={TEST_IDS.MISSED_SCREEN}>
      <div className={styles.meta}>
        <span className={styles.metaLabel}>Retreat passed</span>
      </div>

      <div className={styles.content}>
        <h1 className={styles.heading}>The window has closed.</h1>
        <p className={styles.subtitle}>
          The scheduled time passed while the app was closed.
        </p>

        <div className={styles.rule} />
        <div className={styles.detail}>
          <div className={styles.detailLabel}>Scheduled</div>
          <div className={styles.detailName}>
            {durationSec > 0 ? `${formatDuration(durationSec)} · ` : ""}
            {startLabel} — {endLabel}
          </div>
          <div className={styles.detailDate}>{dateLabel}</div>
        </div>
        <div className={styles.rule} />

        <p className={styles.note}>
          Missed sessions are not replayed. The schedule is a window, not a debt.
        </p>

        <div className={styles.ctas}>
          <PrimaryAction onClick={onScheduleAgain} testId={TEST_IDS.MISSED_RESCHEDULE_BTN}>Choose another morning</PrimaryAction>
          <SecondaryAction onClick={onReturnHome} testId={TEST_IDS.MISSED_RETURN_BTN}>Return</SecondaryAction>
        </div>
      </div>
    </div>
  );
}
